import React from 'react';
import PropTypes from 'prop-types';
import { LuPlus, LuTags, LuTrash2, LuX } from 'react-icons/lu';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import GlassIcon from '../../../components/ui/GlassIcon';
import { Field, TextInput } from '../../../components/form/FormControls';
import useStockText from '../stockText';

// Kategoriyle karışan eski tip adları listede gösterilmez
const HIDDEN_TYPES = new Set(['Sarf Malzemesi', 'Demirbaş']);

/**
 * "Stok Tipi Ekle": adds a type and lists the existing ones with how many
 * stocks use them; a type no stock uses can be deleted (after a confirm).
 */
const StockTypesModal = ({ isOpen, value, setValue, stockTypes, stocks, onAdd, onDelete, pendingDelete, onConfirmDelete, onCancelDelete, onClose }) => {
  const { t, language, label } = useStockText();
  const types = stockTypes.filter((type) => !HIDDEN_TYPES.has(type.name));

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="md"
        title={label('types.title')}
        subtitle={label('types.subtitle')}
        closeLabel={t('common.close')}
        lang={language}
        icon={(
          <GlassIcon gradient="tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30" size="xs">
            <LuTags aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        footer={(
          <>
            <Button variant="ghost" onClick={onClose}>{label('common.cancel')}</Button>
            <Button variant="primary" onClick={onAdd}>
              <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('types.add')}
            </Button>
          </>
        )}
      >
        <form
          className="tw-space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            onAdd();
          }}
        >
          <Field label={label('types.name')} htmlFor="stockTypeName" required>
            <TextInput id="stockTypeName" value={value} onChange={(e) => setValue(e.target.value)} placeholder={label('types.placeholder')} autoFocus />
          </Field>

          {types.length > 0 && (
            <div className="tw-space-y-2">
              <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500">{label('types.existing', { count: types.length })}</p>
              <ul className="tw-flex tw-max-h-60 tw-flex-wrap tw-gap-2 tw-overflow-y-auto tw-rounded-2xl tw-bg-slate-500/[0.05] tw-p-3 glass-scrollbar dark:tw-bg-white/[0.03]">
                {types.map((type) => {
                  const count = stocks.filter((s) => s['Stok Tipi'] === type.name).length;
                  return (
                    <li key={type.id} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-slate-200/80 tw-bg-white tw-py-1 tw-pl-3 tw-pr-1.5 tw-text-sm tw-text-slate-700 dark:tw-border-white/10 dark:tw-bg-white/[0.05] dark:tw-text-slate-200">
                      <span>{type.name}</span>
                      {count > 0 ? (
                        <span title={label('types.inUseHint')} className="tw-rounded-full tw-bg-slate-900/[0.06] tw-px-1.5 tw-py-px tw-text-[11px] tw-font-bold tw-tabular-nums tw-text-slate-500 dark:tw-bg-white/10 dark:tw-text-slate-300">{count}</span>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => onDelete(e, type.id, type.name)}
                          aria-label={label('types.delete', { name: type.name })}
                          title={label('types.delete', { name: type.name })}
                          className="tw-grid tw-h-5 tw-w-5 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-rose-500 tw-transition-colors hover:tw-bg-rose-500/10"
                        >
                          <LuX aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
              <p className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('types.help')}</p>
            </div>
          )}
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={!!pendingDelete}
        tone="danger"
        icon={LuTrash2}
        title={label('types.confirmDelete.title')}
        message={label('types.confirmDelete.message', { name: pendingDelete?.name || '' })}
        confirmLabel={label('types.confirmDelete.confirm')}
        cancelLabel={label('common.cancel')}
        onConfirm={onConfirmDelete}
        onCancel={onCancelDelete}
      />
    </>
  );
};

StockTypesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  value: PropTypes.string.isRequired,
  setValue: PropTypes.func.isRequired,
  stockTypes: PropTypes.array.isRequired,
  stocks: PropTypes.array.isRequired,
  onAdd: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  pendingDelete: PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
  onConfirmDelete: PropTypes.func.isRequired,
  onCancelDelete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default StockTypesModal;
